import type { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot } from '@angular/router';

import { inject } from '@angular/core';
import { environment } from '@env/environment';
import { buildUrl } from '@loyalty/utils-angular/core';
import { isLogged, jwtSet } from '@loyalty/utils-angular/stores';
import { AuthenticationWebservice } from '@loyalty/utils-angular/webservices';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { of } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';

export const loggedGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authenticationWebservice = inject(AuthenticationWebservice);
  const store = inject(Store);

  const loginToken = route.queryParamMap.get('login-token');

  /*
    Check if the "login-token" query parameter exists in the URL.
    If it does, use this token to authenticate with the API
    and store the JWT in the NgRx State.
  */
  if (loginToken) {
    return authenticationWebservice.getJwtFromLoginToken(loginToken).pipe(
      tap((jwtTokens) => {
        store.dispatch(jwtSet({ jwtTokens }));
      }),
      catchError(() => {
        ssoRedirect(state.url);
        return of(false);
      }),
      map(() => true)
    );
  }

  /*
    Check if the user has a jwt cookie
   */
  const hasJwt = inject(CookieService).check('wizbii_bo');

  /*
    If the user has a jwt cookie, check if wait for the user to be logged in
  */
  if (hasJwt) {
    return store.select(isLogged).pipe(
      filter((logged) => logged),
      take(1)
    );
  }

  /*
    If the user has not a jwt cookie, redirect to the SSO
  */
  ssoRedirect(state.url);
  return false;
};

const ssoRedirect = (url: string): void => {
  window.open(
    buildUrl(environment.api.sso, {
      redirect_url: `https://${environment.appUrl}${url}`,
      appId: environment.applicationId,
    }),
    '_self'
  );
};
